// ARCHIVO COMPARTIDO app/web — editar en ambos repos. Verificar con `npm run check:shared` (TemControlApp).
//
// Detección de jornadas ABIERTAS: días en los que el trabajador fichó entrada
// y nunca fichó la salida.
//
// La regla de negocio es: no puedes empezar una jornada nueva mientras tengas
// una anterior sin cerrar. Primero cierras la que dejaste abierta (con el
// fichaje manual, que queda pendiente de aprobación) y luego ya fichas hoy.
//
// POR QUÉ ESTE FICHERO EXISTE
// Antes esto vivía suelto dentro del Dashboard, duplicado en la app y en la
// web. Duplicar una regla en dos sitios sin un origen común es como se
// desincronizan: acaban divergiendo y el trabajador ve una cosa en el móvil y
// otra en el navegador. Este módulo es idéntico en TemControlApp y
// TemControlFront: si tocas uno, toca el otro.
//
// QUÉ CORRIGE RESPECTO A LA VERSIÓN ANTERIOR
//   1. Antes solo miraba AYER (`fecha - 1`). Si te dejabas el viernes abierto y
//      volvías el lunes, no te bloqueaba nada — y ése es justamente el caso más
//      frecuente, porque el día que te olvidas de fichar salida suele ser el
//      último antes de librar. Ahora mira TODOS los días anteriores dentro de
//      la ventana.
//   2. Antes ignoraba los días sin jornada asignada en `workhours` (descansos).
//      Si fichabas un sábado suelto y te dejabas la salida, no se detectaba.
//      Ahora el criterio son los FICHAJES, no el horario teórico: si hay una
//      entrada sin su salida, hay una jornada abierta, tuvieras turno o no.
//   3. Los días (y "hoy") se deciden en la zona horaria del TRABAJADOR
//      (`timeZone`, src/lib/workerTime.js), no en la del dispositivo: un
//      fichaje a las 23:30 en Canarias es de ese día aunque el móvil esté en
//      hora de Madrid.
//
// LÍMITE: esto es detección en cliente, sobre los eventos ya cargados. No
// sustituye a la validación en servidor — el backend debe rechazar el CLOCK_IN
// si hay una jornada abierta, o la regla se salta desde otro dispositivo o
// llamando directamente a la API.

import { DEFAULT_TIME_ZONE, dayKeyInZone, addDaysToDayKey, dowOfDayKey } from './workerTime';

// Hora sugerida para la salida que falta. Se prefiere el fin del turno de
// tarde (`out2`); si el trabajador solo tiene turno de mañana, `out1`. Si ese
// día no tenía jornada asignada (un sábado suelto, por ejemplo) no hay horario
// del que tirar y caemos a las 18:00, que es solo un valor de partida
// editable en el modal.
const suggestedExitTime = (slot) => {
  const trim = (v) => (v ? String(v).slice(0, 5) : null);
  return trim(slot?.out2) || trim(slot?.out1) || '18:00';
};

// Devuelve la jornada abierta MÁS ANTIGUA anterior a hoy, o null si no hay.
//
// Se devuelve la más antigua a propósito: si alguien arrastra varios días sin
// cerrar, los resuelve en orden cronológico. Al cerrar uno, la siguiente
// llamada devuelve el siguiente, hasta vaciar el atraso.
//
//   events         → eventos del trabajador (los que haya cargados; solo se
//                    miran CLOCK_IN / CLOCK_OUT).
//   clockInType    → valor de EVENT_TYPES.CLOCK_IN
//   clockOutType   → valor de EVENT_TYPES.CLOCK_OUT
//   workHoursByDow → Map<dow 0..6, slot>, solo para sugerir la hora.
//   lookbackDays   → cuántos días hacia atrás se miran. Tiene que ir alineado
//                    con la ventana de eventos que carga la pantalla: mirar
//                    más atrás de lo que se ha descargado no encuentra nada.
//   today          → inyectable para tests (Date, timestamp o 'YYYY-MM-DD').
//                    Por defecto, ahora.
//   timeZone       → zona del trabajador (timeZoneForWorker). Por defecto
//                    Europe/Madrid.
// La `date` devuelta es el día en la zona del trabajador ('YYYY-MM-DD').
export const findOldestOpenClockDay = ({
  events,
  clockInType,
  clockOutType,
  workHoursByDow = null,
  lookbackDays = 30,
  today = new Date(),
  timeZone = DEFAULT_TIME_ZONE,
} = {}) => {
  if (!Array.isArray(events) || events.length === 0) return null;

  const todayIso = dayKeyInZone(today, timeZone);
  if (!todayIso) return null;
  const floorIso = addDaysToDayKey(todayIso, -lookbackDays);

  // Agrupamos los fichajes de entrada/salida por día en la zona del trabajador.
  const byDay = new Map();
  for (const e of events) {
    if (e?.eventType !== clockInType && e?.eventType !== clockOutType) continue;
    const when = new Date(e.timestamp);
    if (Number.isNaN(when.getTime())) continue;
    const iso = dayKeyInZone(when, timeZone);
    // Hoy NO cuenta: tener la jornada de hoy abierta es lo normal, estás
    // trabajando. Y más atrás de la ventana no tenemos datos fiables.
    if (iso >= todayIso || iso < floorIso) continue;
    if (!byDay.has(iso)) byDay.set(iso, []);
    byDay.get(iso).push(e);
  }
  if (byDay.size === 0) return null;

  // Un día está ABIERTO si su último fichaje del día es una entrada. Comparar
  // el último evento (y no contar entradas vs salidas) tolera las jornadas
  // partidas: entrada, salida, entrada, salida son dos pares cerrados.
  const openDays = [];
  for (const [iso, dayEvents] of byDay) {
    dayEvents.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    const last = dayEvents[dayEvents.length - 1];
    if (last.eventType === clockInType) openDays.push(iso);
  }
  if (openDays.length === 0) return null;

  openDays.sort();
  const date = openDays[0];

  // Día de la semana calculado sobre la clave de día (puro, sin zona del
  // dispositivo ni cambios de hora).
  const dow = dowOfDayKey(date);
  const slot = workHoursByDow?.get ? workHoursByDow.get(dow) : null;

  return {
    date,
    suggestedTime: suggestedExitTime(slot),
    pendingCount: openDays.length,
  };
};
